import api from "../../../shared/api.jsx";
import { logError } from "../../../shared/utils/logger.js";
import {
  coerceLegacyDateRangeToFilters,
  createDefaultDashboardFilters,
  serializeDashboardFilters,
} from "../dashboardFilters.js";

/**
 * Dashboard Service - API calls for dashboard data
 */

/**
 * Get dashboard data
 * @param {Object|number} filters - Dashboard filter object or legacy day range
 * @returns {Promise<Object>} Dashboard data
 */
export const getDashboardData = async (filters = createDefaultDashboardFilters()) => {
  const queryParams = buildDashboardQueryParams(filters);

  try {
    const response = await api.get("/dashboard", { params: queryParams });
    return response;
  } catch (error) {
    logError("Error fetching dashboard data", {
      filters: queryParams,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch dashboard data",
    };
  }
};

/**
 * Get dashboard statistics
 * @param {Object|number} filters - Dashboard filter object or legacy day range
 * @returns {Promise<Object>} Dashboard statistics
 */
export const getDashboardStats = async (filters = createDefaultDashboardFilters()) => {
  const queryParams = buildDashboardQueryParams(filters);

  try {
    const response = await api.get("/dashboard/stats", { params: queryParams });
    return response.data;
  } catch (error) {
    logError("Error fetching dashboard stats", {
      filters: queryParams,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch dashboard stats",
    };
  }
};

export const getDashboardDrilldown = async ({
  metricKey,
  filters = createDefaultDashboardFilters(),
  limit = 25,
} = {}) => {
  const queryParams = {
    ...buildDashboardQueryParams(filters),
    limit,
  };

  try {
    return await api.get(`/dashboard/drilldown/${metricKey}`, { params: queryParams });
  } catch (error) {
    logError("Error fetching dashboard drilldown", {
      metricKey,
      filters: queryParams,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch dashboard drilldown",
    };
  }
};

/**
 * Get platform status
 * @returns {Promise<Object>} Platform status
 */
export const getPlatformStatus = async () => {
  try {
    const response = await api.get("/dashboard/platform-status");
    return response.data;
  } catch (error) {
    logError("Error fetching platform status", {
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch platform status",
    };
  }
};

/**
 * Get funnel analytics for specific date range
 * @param {number} dateRange - Number of days for analytics
 * @returns {Promise<Object>} Funnel analytics data
 */
export const getFunnelAnalytics = async (dateRange = 30) => {
  try {
    const response = await api.get(
      `/analytics/funnel/overview?dateRange=${dateRange}`
    );
    return response.data;
  } catch (error) {
    logError("Error fetching funnel analytics", {
      dateRange,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch funnel analytics",
    };
  }
};

/**
 * Get campaign performance for specific date range
 * @param {number} dateRange - Number of days for analytics
 * @returns {Promise<Object>} Campaign performance data
 */
export const getCampaignPerformance = async (dateRange = 30) => {
  try {
    const response = await api.get(
      `/analytics/funnel/campaigns?dateRange=${dateRange}`
    );
    return response.data;
  } catch (error) {
    logError("Error fetching campaign performance", {
      dateRange,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch campaign performance",
    };
  }
};


/**
 * Ops & Founder daily/range report (metrics + AI summary)
 * @param {{ asOf?: string, from?: string, to?: string }} params
 */
export const getOpsFounderReport = async (params = {}) => {
  const queryParams = {};
  if (params.asOf) queryParams.asOf = params.asOf;
  if (params.from) queryParams.from = params.from;
  if (params.to) queryParams.to = params.to;
  if (params.startDate) queryParams.startDate = params.startDate;
  if (params.endDate) queryParams.endDate = params.endDate;

  try {
    return await api.get("/dashboard/ops-founder-report", { params: queryParams });
  } catch (error) {
    logError("Error fetching ops founder report", {
      params: queryParams,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch ops founder report",
    };
  }
};

/**
 * Lead Intelligence dashboard aggregations (Lead Pulse + Leads header)
 */
export const getLeadIntelligenceDashboard = async (
  filters = createDefaultDashboardFilters()
) => {
  const queryParams = buildDashboardQueryParams(filters);
  try {
    return await api.get("/dashboard/lead-intelligence", { params: queryParams });
  } catch (error) {
    logError("Error fetching lead intelligence dashboard", {
      filters: queryParams,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch lead intelligence dashboard",
    };
  }
};

function buildDashboardQueryParams(filters) {
  if (typeof filters === "number" || typeof filters === "string") {
    return serializeDashboardFilters(
      coerceLegacyDateRangeToFilters(filters, createDefaultDashboardFilters())
    );
  }

  return serializeDashboardFilters(filters);
}
